import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {
  DataGridPro,
  GridCallbackDetails,
  GridCellParams,
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
  GridRowParams,
  GridSortModel,
  MuiEvent,
} from '@mui/x-data-grid-pro';

import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';

import { SortModel } from '@/data/models/sort-model.model';
import { TripLegSearchMenu } from '@/data/requests/trip-legs';
import { TripLeg, TripLegAddress } from '@/data/models/reservation.model';
import { TripLegEventType } from '@/services/webSocketService/updateTripLeg';

import * as lookupSlice from '@/data/lookup.slice';
import * as triplegSlice from '@/data/tripleg.slice';

import dateUtils from '@/services/DateUtils';

import DiscussionDialog from './DiscussionDialog';
import { ButtonException, CellActions } from '@/components/tables/DataGridPro';
import TextSearch from '@/components/controls/TextSearch';

const PREFIX = 'Discussions';

const classes = {
  container: `${PREFIX}-container`,
  grid: `${PREFIX}-grid`,
  actions: `${PREFIX}-actions`,
  filter: `${PREFIX}-filter`,
  subitem: `${PREFIX}-subitem`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(3),
    '& > *:not(:last-child)': {
      marginBottom: theme.spacing(3), // Adds bottom margin to all child components except the last one
    },
    boxShadow: '0px 0px 10px #dfdfdf',
  },

  [`& .${classes.grid}`]: {
    borderStyle: 'none',
    '& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-columnHeaderTitleContainerContent, & .MuiDataGrid-columnHeaderTitleContainerContent .MuiTypography-root':
      {
        color: theme.palette.lightTextPrimary.main,
        fontFamily: 'Source Sans Pro',
        fontSize: 14,
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: 24,
        letterSpacing: 0.17,
      },
    '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus':
      {
        outline: 'none',
      },
    '& .MuiDataGrid-cell:first-of-type': {
      padding: 0,
    },
  },
  [`& .${classes.actions}`]: {
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1),
  },
  [`& .${classes.filter}`]: {
    color: theme.palette.text.secondary,
    borderWidth: 0,
  },
  [`& .${classes.subitem}`]: {
    fontFamily: 'Source Sans Pro',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '143%',
    letterSpacing: '0.17px',
    color: 'rgba(1, 16, 18, 0.6)',
  },
}));

export default function Discussions() {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const [searchText, setSearchText] = useState('');
  // const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  // const FILTER_ALL = 'ALL';
  // const [currentFilter, setCurrentFilter] = useState(FILTER_ALL);
  const [discussionTripLeg, setDiscussionTripLeg] = useState<TripLeg>();
  const [discussionDialogOpen, setDiscussionDialogOpen] = useState(false);
  const discussionTripLegs = useAppSelector(
    triplegSlice.selectDiscussionTripsLegs,
  );
  const loading = useAppSelector(triplegSlice.selectLoadingDiscussionTripLegs);
  const page = useAppSelector(triplegSlice.selectDiscussionPage);
  const pageSize = useAppSelector(triplegSlice.selectDiscussionPageSize);
  const sortModel = useAppSelector(triplegSlice.selectDiscussionPageSortModel);
  const tripLegEventEmitted = useAppSelector(
    triplegSlice.selectTripLegEventEmitted,
  );

  const loadDiscussions = () => {
    dispatch(
      triplegSlice.fetchDiscussionTripLegsAsync({
        page,
        pageSize,
        search: searchText,
        sort: sortModel?.column,
        sortOrder: sortModel?.direction,
      }),
    );
    dispatch(lookupSlice.fetchLookupItemsAsync('tripLegAdjustmentType'));
  };

  useEffect(() => {
    loadDiscussions();
  }, [page, pageSize, sortModel, searchText]);

  const handleDiscuss = (evt: any, row: any) => {
    setDiscussionTripLeg(row);
    setDiscussionDialogOpen(true);
    evt.stopPropagation();
  };

  const viewTrip = (
    params: GridRowParams,
    _evt: MuiEvent<React.MouseEvent>,
    _details: GridCallbackDetails,
  ) => {
    const trip = params.row;
    const url = `/passengers/${trip.passengerUUID}/reservations/${trip.reservationReferenceID}?tripLegID=${trip.tripLegReferenceID}`;
    navigate(url);
  };

  const renderPickupTime = (params: GridCellParams): React.ReactElement => {
    const time = params.row.isWillCall
      ? `${dateUtils.formatAppointmentDate(params.row.appointmentDate, params.row.pickUpAddressTimeZoneIANA)} ${t('discussion.willCall')}`
      : dateUtils.formatPickupDateTime(
          params.row.pickUpDueTimeUTC,
          params.row.pickUpAddressTimeZoneIANA,
        );
    return (
      <Box>
        <Typography>{time}</Typography>
        <Typography className={classes.subitem}>
          {params.row.tripLegReferenceID}
        </Typography>
      </Box>
    );
  };

  const renderAddress = (address: TripLegAddress) => {
    return (
      <Box>
        <Typography>{`${address.addressLine1}`}</Typography>
        <Typography
          className={
            classes.subitem
          }>{`${address.city}, ${address.stateCode}, ${address.zipCode.substring(0, 5)}`}</Typography>
      </Box>
    );
  };

  const renderPickupAddress = (params: GridCellParams): React.ReactElement => {
    const tripLeg = params.row;
    const address = {
      addressLine1: tripLeg.pickUpAddressLine1,
      city: tripLeg.pickUpCity,
      stateCode: tripLeg.pickUpStateCode,
      zipCode: tripLeg.pickUpZipCode,
    };
    return renderAddress(address);
  };

  const renderDropoffAddress = (params: GridCellParams): React.ReactElement => {
    const tripLeg = params.row;
    const address = {
      addressLine1: tripLeg.dropOffAddressLine1,
      city: tripLeg.dropOffCity,
      stateCode: tripLeg.dropOffStateCode,
      zipCode: tripLeg.dropOffZipCode,
    };
    return renderAddress(address);
  };

  const renderMember = (params: GridCellParams): React.ReactElement => {
    const passenger = params.row;
    return (
      <Box>
        <Typography>{`${passenger.passengerFirstName} ${passenger.passengerLastName}`}</Typography>
        <Typography
          className={
            classes.subitem
          }>{`${passenger.clientPassengerID}`}</Typography>
      </Box>
    );
  };

  const renderProvider = (params: GridCellParams): React.ReactElement => {
    const provider = params.row;
    const driver = params.row;
    return (
      <Box>
        <Typography>{`${provider?.providerName || ''}`}</Typography>
        <Typography className={classes.subitem}>
          {`${driver?.driverName || ''}`}
        </Typography>
      </Box>
    );
  };

  const renderException = (params: GridCellParams): React.ReactElement => {
    return <Typography>{t('discussion.underDiscussion')}</Typography>;
  };

  const columns: GridColDef[] = [
    {
      field: 'pickUpDueTimeUTC',
      headerName: t('discussion.pickup'),
      width: 180,
      sortable: true,
      renderCell: renderPickupTime,
    },
    {
      field: 'pickupAddress',
      headerName: t('discussion.pickupAddress'),
      flex: 1,
      minWidth: 240,
      sortable: false,
      renderCell: renderPickupAddress,
    },
    {
      field: 'dropoffAddress',
      headerName: t('discussion.dropoffAddress'),
      flex: 1,
      minWidth: 240,
      sortable: false,
      renderCell: renderDropoffAddress,
    },
    {
      field: 'member',
      headerName: t('discussion.member'),
      minWidth: 240,
      sortable: false,
      renderCell: renderMember,
    },
    {
      field: 'providerName',
      headerName: t('discussion.provider'),
      minWidth: 240,
      sortable: false,
      renderCell: renderProvider,
    },
    {
      field: 'exception',
      headerName: t('discussion.exception'),
      minWidth: 140,
      sortable: false,
      renderCell: renderException,
    },
    {
      field: 'actions',
      headerName: '',
      width: 133,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => (
        <CellActions>
          <ButtonException
            onClick={(evt) => handleDiscuss(evt, params.row)}
            label={t('discussion.discuss')}
          />
        </CellActions>
      ),
    },
  ];

  const paginationChange = (
    model: GridPaginationModel,
    _details: GridCallbackDetails,
  ) => {
    if (model.page !== discussionTripLegs.currentPage - 1) {
      dispatch(triplegSlice.setDiscussionPage(model.page + 1));
    }
    if (model.pageSize !== pageSize) {
      dispatch(triplegSlice.setDiscussionPageSize(model.pageSize));
    }
  };

  const sortModelChange = (
    model: GridSortModel,
    _details: GridCallbackDetails<any>,
  ) => {
    let newModel: SortModel | null = null;
    if (model?.length) {
      newModel = {
        column: model[0].field,
        direction: model[0].sort,
      };
    }
    dispatch(triplegSlice.setDiscussionPage(1));
    dispatch(triplegSlice.setDiscussionPageSortModel(newModel));
  };

  const onSearchChange = (val: string) => {
    setSearchText(val);
    dispatch(triplegSlice.setDiscussionPage(1));
  };

  const closeDiscussionDialog = () => {
    setDiscussionDialogOpen(false);
  };

  useEffect(() => {
    if (
      tripLegEventEmitted &&
      (tripLegEventEmitted.detailType ===
        TripLegEventType.TripLegUnderDiscussion ||
        tripLegEventEmitted.detailType ===
          TripLegEventType.TripLegUnderDiscussionCleared)
    ) {
      loadDiscussions();
      dispatch(triplegSlice.setTripLegEventEmitted(null));
    }
  }, [tripLegEventEmitted]);

  return (
    <StyledBox className={classes.container}>
      <Typography variant="h2">{t('discussion.discuss')}</Typography>
      <Box className={classes.actions}>
        <TextSearch
          id="text-search"
          icon={<SearchIcon />}
          label={t('discussion.search')}
          placeholder={t('discussion.searchPlaceholder')}
          currentSearchFilter={searchText}
          onSearchChange={onSearchChange}></TextSearch>
        {/* TODO: Not really sure what this should do yet so I'm leaving it out */}
        {/* <Button
          className={classes.filter}
          ref={filterButtonRef}
          variant="outlined"
          startIcon={<FilterListOutlinedIcon />}
          onClick={() => setFilterDialogOpen(true)}>
          {t('discussion.filters')}
        </Button> */}
      </Box>
      <DataGridPro
        getRowId={(row) => `${row.tripLegReferenceID}`}
        rows={discussionTripLegs.items}
        rowCount={discussionTripLegs.itemCount}
        columns={columns}
        pagination={true}
        paginationModel={{ page: page - 1, pageSize }}
        paginationMode="server"
        pageSizeOptions={[10, 20, 30]}
        onPaginationModelChange={paginationChange}
        onSortModelChange={sortModelChange}
        onRowClick={viewTrip}
        autoHeight={true}
        loading={loading}
        className={classes.grid}
        disableColumnSelector={true}
        hideFooter={false}
        sortingMode="server"
      />
      {discussionTripLeg && (
        <DiscussionDialog
          tripLegReferenceID={discussionTripLeg.tripLegReferenceID!}
          tripLegID={discussionTripLeg.tripLegID!}
          passengerUUID={discussionTripLeg.passengerUUID}
          open={discussionDialogOpen}
          close={closeDiscussionDialog}
        />
      )}
    </StyledBox>
  );
}
